import FaceStack from "./FaceStack";

// EventRow — the compact event row shared by This Week's 3-event preview and
// the "All events" stacked sheet (AllEventsSheet.jsx). Same badge/title/meta/
// faces/"I'm going" layout either way; callers differ only in which optional
// behaviours they wire up:
//   - ThisWeekTab passes onEdit/onDelete + showManage (cog-gated inline
//     Edit/Delete), no onClick.
//   - AllEventsSheet passes onClick (opens EventDetailSheet) and
//     recurrenceLabel, no onEdit/onDelete/showManage.
//
// Props:
//   ev            — one occurrence from useEvents.js's shape
//   onToggleGoing(ev)
//   rsvpError     — { key, message } | null, key is `${id}-${occurrenceDate}`
//   showManage    — show the Edit/Delete row (only meaningful with onEdit/onDelete)
//   onEdit(ev), onDelete(ev)
//   onClick(ev)   — if present, the whole row becomes a tap target (e.g. open detail)
//   recurrenceLabel — extra string appended to the time · location meta line

const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";
const EVENT_BADGE = { bg: "var(--peach)", ink: "var(--peach-ink)" };

// Shapes a useEvents.js occurrence into the row shape onEditEvent/onDeleteEvent
// (ultimately EventEditorSheet) expect — shared by ThisWeekTab's inline
// Edit/Delete and by AllEventsSheet.jsx.
export function eventEditPayload(ev) {
  return {
    id: ev.id,
    name: ev.title,
    event_date: ev.occurrenceDate,
    event_time: ev.time,
    location: ev.location,
    description: ev.description,
    recurrence: ev.recurring,
    repeat_until: ev.repeatUntil,
  };
}

export function fmtEventBadge(d) {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const target = new Date(d); target.setHours(0, 0, 0, 0);
  if (target.getTime() === now.getTime()) return "Today";
  return target.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

export default function EventRow({ ev, onToggleGoing, rsvpError, showManage, onEdit, onDelete, onClick, recurrenceLabel }) {
  const metaParts = [ev.time, ev.location, recurrenceLabel].filter(Boolean);
  const rowProps = onClick
    ? { onClick: () => onClick(ev), role: "button", tabIndex: 0, style: { ...S.eventRow, cursor: "pointer" } }
    : { style: S.eventRow };
  return (
    <div key={`${ev.id}-${ev.occurrenceDate}`} {...rowProps}>
      <div style={{ ...S.eventBadge, background: EVENT_BADGE.bg, color: EVENT_BADGE.ink }}>
        {fmtEventBadge(ev.occursAt)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={S.eventTitle}>{ev.title}</div>
        <div style={S.eventMeta}>{metaParts.join(" · ")}</div>
        {ev.goingCount > 0 && (
          <div style={S.faceRow}>
            <FaceStack userIds={ev.goingIds} profilesById={ev.goingProfilesById} size={20}/>
            <span style={S.faceCount}>{ev.going ? (ev.goingCount - 1 > 0 ? `You + ${ev.goingCount - 1} going` : "You're going") : `${ev.goingCount} going`}</span>
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
        <button
          style={{ ...S.goingBtn, ...(ev.going ? S.goingOn : null) }}
          onClick={e => { e.stopPropagation(); onToggleGoing?.(ev); }}
        >
          {ev.going ? "Going ✓" : "I'm going"}
        </button>
        {rsvpError?.key === `${ev.id}-${ev.occurrenceDate}` && (
          <div style={S.rsvpErrorText}>{rsvpError.message}</div>
        )}
        {showManage && (onEdit || onDelete) && (
          <div style={{ display: "flex", gap: 10 }}>
            {onEdit && (
              <button type="button" style={S.editEventBtn} onClick={e => { e.stopPropagation(); onEdit(ev); }}>
                Edit
              </button>
            )}
            {onDelete && (
              <button type="button" style={S.deleteEventBtn} onClick={e => { e.stopPropagation(); onDelete(ev); }}>
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export const S = {
  eventRow: { display: "flex", gap: 12, alignItems: "flex-start", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 16, padding: 12, marginBottom: 10 },
  eventBadge: { flexShrink: 0, fontSize: 11, fontWeight: 800, borderRadius: 10, padding: "6px 9px", textAlign: "center", lineHeight: 1.2 },
  eventTitle: { fontSize: 14.5, fontWeight: 700, color: "var(--ink)" },
  eventMeta: { fontSize: 12, color: "var(--stone)", marginTop: 2, marginBottom: 6 },
  goingBtn: { flexShrink: 0, background: "transparent", border: "1px solid var(--border-strong)", color: "var(--stone)", borderRadius: 10, padding: "8px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS, whiteSpace: "nowrap" },
  goingOn: { background: "var(--ink)", color: "var(--white)", border: "none" },
  faceRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12 },
  faceCount: { fontSize: 12, color: "var(--ink)", opacity: 0.75 },
  rsvpErrorText: { fontSize: 10.5, color: "#C8372A", maxWidth: 110, textAlign: "right", lineHeight: 1.3 },
  editEventBtn: { flexShrink: 0, background: "none", border: "none", color: "var(--stone-dim)", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS, padding: "2px 2px" },
  deleteEventBtn: { flexShrink: 0, background: "none", border: "none", color: "#C8372A", fontSize: 11.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS, padding: "2px 2px" },
};
